import * as THREE from 'three';

/**
 * Hand-drawn cartography, painted into canvases at load:
 *  - the realm map (parchment + an ink layer that the shader draws in over time)
 *  - the Project 01 scroll (countdown, redrawn every second)
 *  - the globe's skin
 *  - wood grain for the table
 * Everything here is procedural: no copied artwork.
 */

const FELL = '"IM Fell English", Georgia, serif';
const INK = 'rgba(46, 30, 18, 0.92)';

// Deterministic noise so the map is the same on every visit.
function rng(seed: number) {
  let s = seed >>> 0;
  return () => ((s = (s * 1664525 + 1013904223) >>> 0) / 4294967296);
}
function vnoise(seed: number) {
  const r = rng(seed);
  const table = Array.from({ length: 256 }, () => r());
  const at = (i: number) => table[((i % 256) + 256) % 256];
  return (x: number) => {
    const i = Math.floor(x), f = x - i, u = f * f * (3 - 2 * f);
    return at(i) * (1 - u) + at(i + 1) * u;
  };
}

/** Town positions in map UV (0..1, v down). Buildings rise here. */
export const PLACES = {
  bazaar: [0.43, 0.52], farms: [0.24, 0.64], whisper: [0.29, 0.33], wharf: [0.56, 0.72],
  keep: [0.6, 0.36], mine: [0.73, 0.57], cinder: [0.82, 0.27], church: [0.46, 0.3],
} as const;

function parchment(g: CanvasRenderingContext2D, w: number, h: number, seed: number): void {
  const r = rng(seed);
  g.fillStyle = '#e6d3a8';
  g.fillRect(0, 0, w, h);
  // Mottling and tea stains.
  for (let i = 0; i < 260; i++) {
    const x = r() * w, y = r() * h, rad = 20 + r() * 180;
    const grd = g.createRadialGradient(x, y, 0, x, y, rad);
    const dark = r() < 0.5;
    grd.addColorStop(0, dark ? `rgba(120, 82, 40, ${0.05 + r() * 0.07})` : `rgba(255, 246, 220, ${0.05 + r() * 0.08})`);
    grd.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grd;
    g.fillRect(x - rad, y - rad, rad * 2, rad * 2);
  }
  // Fibres.
  g.globalAlpha = 0.06;
  g.strokeStyle = '#6b4a24';
  for (let i = 0; i < 900; i++) {
    const x = r() * w, y = r() * h, l = 4 + r() * 18, a = r() * Math.PI;
    g.beginPath(); g.moveTo(x, y); g.lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l); g.stroke();
  }
  g.globalAlpha = 1;
  // Darkened, aged edges.
  const e = g.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.3, w / 2, h / 2, Math.max(w, h) * 0.62);
  e.addColorStop(0, 'rgba(0,0,0,0)');
  e.addColorStop(1, 'rgba(92, 56, 24, 0.55)');
  g.fillStyle = e;
  g.fillRect(0, 0, w, h);
}

function coastPath(cx: number, cy: number, rx: number, ry: number, seed: number, wob = 0.22): Path2D {
  const n = vnoise(seed), n2 = vnoise(seed + 7);
  const p = new Path2D();
  const steps = 260;
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * Math.PI * 2;
    const k = 1 + (n(i * 0.09) - 0.5) * wob * 2 + (n2(i * 0.37) - 0.5) * wob * 0.6;
    const x = cx + Math.cos(a) * rx * k, y = cy + Math.sin(a) * ry * k;
    if (i === 0) p.moveTo(x, y); else p.lineTo(x, y);
  }
  p.closePath();
  return p;
}

function label(g: CanvasRenderingContext2D, text: string, x: number, y: number, size: number, italic = true, color = INK): void {
  g.font = `${italic ? 'italic ' : ''}${size}px ${FELL}`;
  g.fillStyle = color;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(text, x, y);
}

function mountain(g: CanvasRenderingContext2D, x: number, y: number, s: number, red = false): void {
  g.strokeStyle = INK;
  g.lineWidth = 2.2;
  g.beginPath();
  g.moveTo(x - s, y); g.lineTo(x - s * 0.15, y - s * 1.3); g.lineTo(x + s, y);
  g.stroke();
  g.lineWidth = 1.2;
  for (let i = 1; i < 4; i++) { // hatching on the shaded side
    g.beginPath(); g.moveTo(x - s * 0.15 + i * s * 0.12, y - s * 1.3 + i * s * 0.28); g.lineTo(x + i * s * 0.12, y); g.stroke();
  }
  if (red) {
    g.fillStyle = 'rgba(176, 40, 22, 0.9)';
    g.beginPath(); g.moveTo(x - s * 0.4, y - s * 1.0); g.lineTo(x - s * 0.15, y - s * 1.3); g.lineTo(x + s * 0.1, y - s * 1.0); g.fill();
  }
}

function tree(g: CanvasRenderingContext2D, x: number, y: number, s: number): void {
  g.strokeStyle = INK; g.lineWidth = 1.6;
  g.beginPath(); g.arc(x, y - s, s * 0.7, 0, Math.PI * 2); g.stroke();
  g.beginPath(); g.moveTo(x, y - s * 0.3); g.lineTo(x, y + s * 0.5); g.stroke();
}

function compass(g: CanvasRenderingContext2D, x: number, y: number, s: number): void {
  g.save();
  g.translate(x, y);
  g.strokeStyle = INK; g.fillStyle = INK; g.lineWidth = 1.5;
  g.beginPath(); g.arc(0, 0, s, 0, Math.PI * 2); g.stroke();
  g.beginPath(); g.arc(0, 0, s * 0.82, 0, Math.PI * 2); g.stroke();
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2, l = i % 2 ? s * 0.55 : s * 1.15;
    g.save(); g.rotate(a);
    g.beginPath(); g.moveTo(0, -l); g.lineTo(s * 0.12, 0); g.lineTo(-s * 0.12, 0); g.closePath();
    if (i % 2) g.stroke(); else g.fill();
    g.restore();
  }
  label(g, 'N', 0, -s * 1.42, s * 0.42, false);
  g.restore();
}

/** Returns [blank parchment, inked map]: the shader draws the ink in. */
export function mapTextures(): [THREE.CanvasTexture, THREE.CanvasTexture] {
  const W = 2048, H = 1384;
  const blank = document.createElement('canvas');
  blank.width = W; blank.height = H;
  parchment(blank.getContext('2d')!, W, H, 11);
  const inked = document.createElement('canvas');
  inked.width = W; inked.height = H;
  const g = inked.getContext('2d')!;
  g.drawImage(blank, 0, 0);
  const P = (k: keyof typeof PLACES) => [PLACES[k][0] * W, PLACES[k][1] * H] as const;

  // Sea hatching: echo lines around the coast, fading outward.
  const coast = coastPath(W * 0.52, H * 0.5, W * 0.37, H * 0.36, 4);
  for (let i = 6; i >= 1; i--) {
    g.save();
    g.translate(W * 0.52, H * 0.5);
    const k = 1 + i * 0.035;
    g.scale(k, k);
    g.translate(-W * 0.52, -H * 0.5);
    g.strokeStyle = `rgba(46, 30, 18, ${0.28 - i * 0.035})`;
    g.lineWidth = 1.4;
    g.stroke(coast);
    g.restore();
  }
  g.fillStyle = 'rgba(214, 190, 140, 0.55)';
  g.fill(coast);
  g.strokeStyle = INK; g.lineWidth = 3.2;
  g.stroke(coast);
  // A lake by the wharf.
  const lake = coastPath(P('wharf')[0] + 40, P('wharf')[1] - 60, 90, 55, 9, 0.18);
  g.fillStyle = 'rgba(120, 140, 150, 0.35)'; g.fill(lake);
  g.lineWidth = 2; g.stroke(lake);
  // River from the mountains down to the lake.
  g.lineWidth = 2.4;
  g.beginPath();
  g.moveTo(P('keep')[0] + 60, P('keep')[1] - 80);
  g.bezierCurveTo(P('keep')[0] - 40, P('keep')[1] + 80, P('wharf')[0] + 120, P('wharf')[1] - 200, P('wharf')[0] + 40, P('wharf')[1] - 100);
  g.stroke();
  // Roads: dotted, and the Dragon Road in red.
  const road = (a: readonly [number, number], b: readonly [number, number], red = false) => {
    g.setLineDash([10, 9]);
    g.strokeStyle = red ? 'rgba(170, 40, 22, 0.9)' : INK;
    g.lineWidth = red ? 3 : 2;
    g.beginPath(); g.moveTo(a[0], a[1]);
    g.quadraticCurveTo((a[0] + b[0]) / 2 + (b[1] - a[1]) * 0.15, (a[1] + b[1]) / 2 - (b[0] - a[0]) * 0.1, b[0], b[1]);
    g.stroke();
    g.setLineDash([]);
  };
  road(P('bazaar'), P('farms')); road(P('bazaar'), P('whisper')); road(P('bazaar'), P('wharf'));
  road(P('bazaar'), P('keep')); road(P('keep'), P('mine')); road(P('mine'), P('cinder'), true);
  // Forests and mountains.
  const r = rng(21);
  for (let i = 0; i < 70; i++) tree(g, P('whisper')[0] - 170 + r() * 260, P('whisper')[1] - 70 + r() * 170, 9 + r() * 5);
  for (let i = 0; i < 22; i++) tree(g, P('farms')[0] - 160 + r() * 90, P('farms')[1] + 40 + r() * 110, 8 + r() * 4);
  for (let i = 0; i < 16; i++) mountain(g, P('cinder')[0] - 230 + r() * 330, P('cinder')[1] + 30 + r() * 200, 20 + r() * 16);
  mountain(g, P('cinder')[0], P('cinder')[1] + 20, 64, true);
  for (let i = 0; i < 8; i++) mountain(g, P('mine')[0] + 40 + r() * 140, P('mine')[1] + 30 + r() * 90, 16 + r() * 10);
  // Labels.
  label(g, 'Grand Bazaar', P('bazaar')[0], P('bazaar')[1] + 92, 40);
  label(g, 'Hollow Farms', P('farms')[0], P('farms')[1] + 88, 34);
  label(g, 'Whisperwood', P('whisper')[0] - 30, P('whisper')[1] - 108, 36);
  label(g, 'Lakeside Wharf', P('wharf')[0] - 20, P('wharf')[1] + 86, 32);
  label(g, 'The Old Keep', P('keep')[0], P('keep')[1] - 96, 34);
  label(g, 'Ironvein Mine', P('mine')[0] + 10, P('mine')[1] + 96, 32);
  label(g, 'Mount Cinder', P('cinder')[0], P('cinder')[1] - 70, 42, true, 'rgba(150, 30, 16, 0.95)');
  label(g, 'the Dragon Road', (P('mine')[0] + P('cinder')[0]) / 2 + 60, (P('mine')[1] + P('cinder')[1]) / 2 + 10, 26, true, 'rgba(150, 30, 16, 0.85)');
  label(g, 'The Realm of Burnwood', W * 0.24, H * 0.1, 64, false);
  g.strokeStyle = INK; g.lineWidth = 1.4;
  g.beginPath(); g.moveTo(W * 0.24 - 290, H * 0.1 + 46); g.lineTo(W * 0.24 + 290, H * 0.1 + 46); g.stroke();
  label(g, 'drawn by the studio, to be burned by it', W * 0.24, H * 0.1 + 80, 26);
  compass(g, W * 0.88, H * 0.82, 86);
  // Sea creature doodle, a cartographer's tradition.
  g.lineWidth = 2; g.strokeStyle = INK;
  g.beginPath();
  for (let i = 0; i < 3; i++) g.arc(W * 0.11 + i * 46, H * 0.78, 20, Math.PI, 0);
  g.stroke();
  label(g, 'here be dragons', W * 0.15, H * 0.84, 26);

  const t1 = new THREE.CanvasTexture(blank), t2 = new THREE.CanvasTexture(inked);
  for (const t of [t1, t2]) { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8; }
  return [t1, t2];
}

/** The Project 01 scroll: redraw with draw(text). */
export function scrollTexture(): { tex: THREE.CanvasTexture; draw: (countdown: string) => void } {
  const W = 1024, H = 720;
  const base = document.createElement('canvas');
  base.width = W; base.height = H;
  parchment(base.getContext('2d')!, W, H, 31);
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d')!;
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  const draw = (countdown: string) => {
    g.drawImage(base, 0, 0);
    label(g, 'Project 01', W / 2, 150, 92, false);
    g.strokeStyle = INK; g.lineWidth = 1.6;
    g.beginPath(); g.moveTo(W / 2 - 220, 214); g.lineTo(W / 2 + 220, 214); g.stroke();
    label(g, 'lights up in', W / 2, 270, 38);
    label(g, countdown, W / 2, 380, 86, false, 'rgba(120, 26, 12, 0.95)');
    label(g, 'the first of January, two thousand twenty-seven', W / 2, 500, 32);
    label(g, '— sealed by Burnwood —', W / 2, 600, 28);
    tex.needsUpdate = true;
  };
  draw('');
  return { tex, draw };
}

export function globeTexture(): THREE.CanvasTexture {
  const W = 1024, H = 512;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d')!;
  parchment(g, W, H, 51);
  g.fillStyle = 'rgba(70, 92, 104, 0.32)';
  g.fillRect(0, 0, W, H);
  const r = rng(5);
  for (let i = 0; i < 5; i++) {
    const p = coastPath(80 + r() * (W - 160), 110 + r() * (H - 220), 70 + r() * 120, 50 + r() * 80, 100 + i, 0.3);
    g.fillStyle = 'rgba(214, 190, 140, 0.95)'; g.fill(p);
    g.strokeStyle = INK; g.lineWidth = 2.4; g.stroke(p);
  }
  g.strokeStyle = 'rgba(46, 30, 18, 0.35)'; g.lineWidth = 1;
  for (let i = 1; i < 12; i++) { g.beginPath(); g.moveTo((i / 12) * W, 0); g.lineTo((i / 12) * W, H); g.stroke(); }
  for (let i = 1; i < 6; i++) { g.beginPath(); g.moveTo(0, (i / 6) * H); g.lineTo(W, (i / 6) * H); g.stroke(); }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export function woodTexture(): THREE.CanvasTexture {
  const W = 1024, H = 1024;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d')!;
  g.fillStyle = '#3a2416';
  g.fillRect(0, 0, W, H);
  const n = vnoise(77), r = rng(78);
  // Planks along x, each with its own tone and wavy grain.
  const planks = 6;
  for (let p = 0; p < planks; p++) {
    const y0 = (p / planks) * H, ph = H / planks;
    g.fillStyle = `hsl(${22 + r() * 8}, ${38 + r() * 10}%, ${16 + r() * 7}%)`;
    g.fillRect(0, y0, W, ph);
    for (let i = 0; i < 70; i++) {
      const yy = y0 + r() * ph, amp = 2 + r() * 6, f = 0.004 + r() * 0.01;
      g.strokeStyle = `rgba(${r() < 0.5 ? '20,10,4' : '120,80,48'}, ${0.08 + r() * 0.16})`;
      g.lineWidth = 0.6 + r() * 1.6;
      g.beginPath();
      for (let x = 0; x <= W; x += 8) g.lineTo(x, yy + Math.sin(x * f + i) * amp + (n(x * 0.01 + i) - 0.5) * 8);
      g.stroke();
    }
    g.fillStyle = 'rgba(0,0,0,0.55)';
    g.fillRect(0, y0, W, 2);
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 8;
  return t;
}

// ---------- What we make: five ink-drawn cards (plus a shared back)
export const CARDS = [
  { n: 'I', title: 'AI & RL experiments', line: 'Enemies that learn. Sometimes they learn to cheat. We keep those.' },
  { n: 'II', title: 'Souls-likes', line: 'Hard, fair, a little cruel. You will die. You will come back.' },
  { n: 'III', title: 'Cozy worlds', line: 'Places you don’t want to leave. No timers yelling at you.' },
  { n: 'IV', title: 'Browser games', line: 'Click a link and you’re in. No 80 GB download, no launcher.' },
  { n: 'V', title: 'Roblox', line: 'Where the next generation plays. We’re building there too.' },
];

function wrap(g: CanvasRenderingContext2D, text: string, x: number, y: number, max: number, lh: number): void {
  const words = text.split(' ');
  let line = '';
  for (const w of words) {
    const test = line ? line + ' ' + w : w;
    if (g.measureText(test).width > max && line) { g.fillText(line, x, y); y += lh; line = w; } else line = test;
  }
  g.fillText(line, x, y);
}

function glyph(g: CanvasRenderingContext2D, i: number, cx: number, cy: number, s: number): void {
  g.save();
  g.translate(cx, cy);
  g.strokeStyle = INK; g.fillStyle = INK; g.lineWidth = 4; g.lineCap = 'round'; g.lineJoin = 'round';
  const RED = 'rgba(170, 40, 22, 0.95)';
  if (i === 0) { // a little learning network, one node lit
    const nodes = [[-1, -0.6], [-1, 0], [-1, 0.6], [0, -0.3], [0, 0.3], [1, 0]].map(([x, y]) => [x * s * 0.9, y * s]);
    for (const a of nodes.slice(0, 3)) for (const b of nodes.slice(3, 5)) { g.beginPath(); g.moveTo(a[0], a[1]); g.lineTo(b[0], b[1]); g.stroke(); }
    for (const a of nodes.slice(3, 5)) { g.beginPath(); g.moveTo(a[0], a[1]); g.lineTo(nodes[5][0], nodes[5][1]); g.stroke(); }
    nodes.forEach(([x, y], k) => { g.beginPath(); g.arc(x, y, s * 0.13, 0, 7); g.fillStyle = k === 5 ? RED : '#e6d3a8'; g.fill(); g.stroke(); });
  } else if (i === 1) { // a sword planted in a bonfire
    g.beginPath(); g.moveTo(0, -s * 1.1); g.lineTo(0, s * 0.5); g.stroke();
    g.beginPath(); g.moveTo(-s * 0.35, -s * 0.55); g.lineTo(s * 0.35, -s * 0.55); g.stroke();
    g.beginPath(); g.arc(0, -s * 1.2, s * 0.08, 0, 7); g.fill();
    for (let k = -2; k <= 2; k++) { g.beginPath(); g.moveTo(k * s * 0.2, s * 0.85); g.quadraticCurveTo(k * s * 0.3, s * 0.45, k * s * 0.12, s * 0.2 + Math.abs(k) * s * 0.1); g.stroke(); }
    g.beginPath(); g.moveTo(-s * 0.8, s * 0.9); g.lineTo(s * 0.8, s * 0.9); g.stroke();
  } else if (i === 2) { // a steaming mug
    g.strokeRect(-s * 0.55, -s * 0.2, s * 0.9, s * 1.0);
    g.beginPath(); g.arc(s * 0.45, s * 0.3, s * 0.25, -Math.PI / 2, Math.PI / 2); g.stroke();
    for (let k = -1; k <= 1; k++) { g.beginPath(); g.moveTo(k * s * 0.25 - s * 0.1, -s * 0.35); g.bezierCurveTo(k * s * 0.25 + s * 0.1, -s * 0.6, k * s * 0.25 - s * 0.25, -s * 0.8, k * s * 0.25, -s * 1.05); g.stroke(); }
  } else if (i === 3) { // a browser window with a cursor
    g.strokeRect(-s, -s * 0.75, s * 2, s * 1.5);
    g.beginPath(); g.moveTo(-s, -s * 0.4); g.lineTo(s, -s * 0.4); g.stroke();
    for (let k = 0; k < 3; k++) { g.beginPath(); g.arc(-s * 0.8 + k * s * 0.2, -s * 0.575, s * 0.06, 0, 7); g.fill(); }
    g.beginPath(); g.moveTo(-s * 0.1, -s * 0.15); g.lineTo(-s * 0.1, s * 0.55); g.lineTo(s * 0.08, s * 0.38); g.lineTo(s * 0.24, s * 0.68); g.lineTo(s * 0.34, s * 0.62); g.lineTo(s * 0.18, s * 0.33); g.lineTo(s * 0.4, s * 0.3); g.closePath();
    g.fillStyle = RED; g.fill(); g.stroke();
  } else { // stacked building bricks
    const brick = (x: number, y: number) => { g.strokeRect(x, y, s * 0.8, s * 0.5); for (let k = 0; k < 2; k++) { g.beginPath(); g.ellipse(x + s * 0.2 + k * s * 0.4, y - s * 0.05, s * 0.12, s * 0.05, 0, 0, 7); g.stroke(); } };
    brick(-s * 0.8, s * 0.3); brick(0, s * 0.3); brick(-s * 0.4, -s * 0.25);
  }
  g.restore();
}

export function cardTextures(): { fronts: THREE.CanvasTexture[]; back: THREE.CanvasTexture } {
  const W = 512, H = 768;
  const make = (paint: (g: CanvasRenderingContext2D) => void) => {
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const g = c.getContext('2d')!;
    parchment(g, W, H, 61);
    g.strokeStyle = INK; g.lineWidth = 3; g.strokeRect(22, 22, W - 44, H - 44);
    g.lineWidth = 1.2; g.strokeRect(34, 34, W - 68, H - 68);
    paint(g);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
    return t;
  };
  const fronts = CARDS.map((card, i) => make((g) => {
    label(g, card.n, W / 2, 86, 46, false);
    glyph(g, i, W / 2, 300, 92);
    label(g, card.title, W / 2, 500, card.title.length > 16 ? 40 : 48, false);
    g.font = `italic 28px ${FELL}`;
    g.fillStyle = INK;
    wrap(g, card.line, W / 2, 568, W - 120, 36);
  }));
  const back = make((g) => {
    g.save();
    g.translate(W / 2, H / 2 - 30);
    g.scale(2.6, 2.6);
    g.translate(-56, -66);
    g.fillStyle = 'rgba(46, 30, 18, 0.9)';
    g.fill(new Path2D('M56 20c4 14 22 24 22 46a22 22 0 0 1-44 0c0-12 8-18 10-28 4 8 10 10 10 10s-6-14 2-28z'));
    g.restore();
    label(g, 'Burnwood', W / 2, H - 110, 44, true);
  });
  return { fronts, back };
}

// ---------- The industry's junk, on cardboard, waiting to be burned
export function boxTexture(text: string): THREE.CanvasTexture {
  const W = 512, H = 512;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d')!;
  g.fillStyle = '#9a7a52';
  g.fillRect(0, 0, W, H);
  const r = rng(text.length * 13);
  for (let i = 0; i < 400; i++) {
    g.fillStyle = r() < 0.5 ? `rgba(60, 40, 20, ${0.04 + r() * 0.06})` : `rgba(200, 170, 120, ${0.04 + r() * 0.06})`;
    g.fillRect(r() * W, r() * H, 2 + r() * 30, 1 + r() * 3);
  }
  g.strokeStyle = 'rgba(60, 40, 20, 0.35)'; g.lineWidth = 6;
  g.beginPath(); g.moveTo(0, H / 2); g.lineTo(W, H / 2); g.stroke();
  g.save();
  g.translate(W / 2, H / 2);
  g.rotate(-0.12);
  g.strokeStyle = 'rgba(150, 24, 18, 0.85)'; g.lineWidth = 8;
  g.strokeRect(-215, -70, 430, 140);
  g.font = `500 ${text.length > 10 ? 44 : 58}px "Martian Mono", monospace`;
  g.fillStyle = 'rgba(150, 24, 18, 0.85)';
  g.textAlign = 'center'; g.textBaseline = 'middle';
  g.fillText(text, 0, 4);
  g.restore();
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

// ---------- A letter from the founder
export const LETTER = [
  'Hi, I’m Sarthak.',
  '',
  'By day I’m an AI engineer. Before that I tuned',
  'camera pipelines for Galaxy phones at Samsung R&D.',
  'I once shipped a bug fix from Annapurna Base Camp,',
  'at seventeen thousand feet. On purpose.',
  '',
  'Burnwood is the studio I always wanted to buy',
  'games from: small, honest, and a bit dangerous.',
  'We keep it stupid simple, and we make it bite.',
];

export function letterTexture(): THREE.CanvasTexture {
  const W = 1024, H = 1320;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d')!;
  parchment(g, W, H, 91);
  g.font = `italic 44px ${FELL}`;
  g.fillStyle = INK;
  g.textAlign = 'left';
  g.textBaseline = 'alphabetic';
  LETTER.forEach((l, i) => g.fillText(l, 90, 170 + i * 74));
  g.font = `italic 64px ${FELL}`;
  g.fillText('— Sarthak', 520, 170 + LETTER.length * 74 + 90);
  label(g, 'founder, Burnwood Interactive', 690, 170 + LETTER.length * 74 + 150, 30);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}
